"use client";

import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type SegmentedControlOption<TValue extends string> = {
  description?: string;
  icon?: ReactNode;
  label: string;
  value: TValue;
};

type SegmentedControlProps<TValue extends string> = {
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  onValueChange: (value: TValue) => void;
  options: readonly SegmentedControlOption<TValue>[];
  value: TValue;
};

export function SegmentedControl<TValue extends string>({
  ariaLabel,
  className,
  disabled = false,
  onValueChange,
  options,
  value,
}: SegmentedControlProps<TValue>) {
  return (
    <div
      aria-label={ariaLabel}
      className={cn("flex flex-wrap gap-2", className)}
      role="group"
    >
      {options.map((option) => {
        const isActive = option.value === value;
        const hasDescription = Boolean(option.description);

        return (
          <Button
            key={option.value}
            aria-pressed={isActive}
            className={cn(
              "h-9 rounded-[10px] border-white/[0.075] bg-white/[0.025] px-3 text-[0.86rem] font-bold text-[var(--rm-text-soft)] hover:border-white/15 hover:bg-white/[0.045] hover:text-foreground",
              hasDescription &&
                "h-auto min-h-12 flex-col items-start gap-0.5 px-3 py-2 text-left leading-tight",
              disabled &&
                "cursor-not-allowed opacity-45 hover:border-white/[0.075] hover:bg-white/[0.025] hover:text-[var(--rm-text-soft)]",
              isActive &&
                "border-primary/40 bg-primary/20 text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] hover:bg-primary/25",
            )}
            disabled={disabled}
            onClick={() => {
              if (disabled) {
                return;
              }
              onValueChange(option.value);
            }}
            type="button"
            variant="outline"
          >
            {hasDescription ? (
              <>
                <span
                  className={cn(
                    option.icon && "inline-flex items-center gap-1.5",
                  )}
                >
                  {option.icon}
                  <span>{option.label}</span>
                </span>
                <span className="text-[0.72rem] font-semibold text-[var(--rm-text-muted)]">
                  {option.description}
                </span>
              </>
            ) : (
              <span
                className={cn(
                  option.icon && "inline-flex items-center gap-1.5",
                )}
              >
                {option.icon}
                <span>{option.label}</span>
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}
